import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getStoredUTM } from '../../hooks/useUTM';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const nextSteps = [
  {
    icon: 'ri-inbox-archive-line',
    title: 'Spracujeme váš dopyt',
    text: 'Pozrieme sa na všetky detaily, ktoré ste nám poslali, a pripravíme sa na hovor.',
  },
  {
    icon: 'ri-user-voice-line',
    title: 'Ozveme sa do 24 hodín',
    text: 'Odpoveď dostanete od reálneho človeka - nie od bota. Zvyčajne skôr.',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Bezplatná konzultácia',
    text: 'Navrhneme konkrétne riešenie pre váš prípad. Bez záväzkov, bez zbytočného predávania.',
  },
];

function CheckmarkCircle() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`relative w-24 h-24 flex items-center justify-center transition-all duration-700 ${
        ready ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
      }`}
    >
      {/* Outer pulse ring */}
      <div className="absolute inset-0 rounded-full bg-orange-500/10 animate-ping" style={{ animationDuration: '2s' }} />
      {/* Middle ring */}
      <div className="absolute inset-2 rounded-full bg-orange-500/15" />
      {/* Inner circle */}
      <div className="relative w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-500">
        <i className="ri-check-line text-white text-3xl" />
      </div>
    </div>
  );
}

export default function DakujemePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utm = getStoredUTM();

  const service = searchParams.get('service') ?? '';
  const utmSource = utm.utm_source ?? '';
  const utmCampaign = utm.utm_campaign ?? '';

  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    // Facebook Pixel - Lead conversion event
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: service || 'Kontaktný formulár',
        content_category: service || 'General',
      });
    }
  }, [service]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const sourceLabel = utmSource
    ? utmSource.charAt(0).toUpperCase() + utmSource.slice(1)
    : null;

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* Minimal Navbar */}
      <header className="flex-shrink-0 border-b border-zinc-800/50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-lg">
              <i className="ri-rocket-2-fill text-white text-sm" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">
              boost<span className="text-orange-500">io</span>
            </span>
          </Link>

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            Späť na hlavnú stránku
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full text-center">

          {/* Checkmark animation */}
          <div className="flex justify-center mb-8">
            <CheckmarkCircle />
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-full">
              <i className="ri-checkbox-circle-line" />
              Dopyt odoslaný
            </span>
            {service && (
              <span className="inline-flex items-center gap-1.5 bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className="ri-service-line" />
                {service}
              </span>
            )}
            {sourceLabel && (
              <span className="inline-flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className="ri-share-line" />
                {sourceLabel}
                {utmCampaign ? ` · ${utmCampaign}` : ''}
              </span>
            )}
          </div>

          {/* Heading */}
          <h1 className="text-5xl md:text-6xl font-black text-white mb-5 leading-tight">
            Ďakujeme!
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed max-w-lg mx-auto mb-12">
            Váš dopyt sme dostali. Ozveme sa vám <strong className="text-white">do 24 hodín</strong> - reálny človek, žiadny automat.
          </p>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-zinc-600 text-xs font-semibold uppercase tracking-widest">Čo sa stane ďalej</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          {/* Next steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
            {nextSteps.map((step, i) => (
              <div
                key={step.title}
                className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 text-left"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-zinc-800/80 border border-zinc-700/60 flex-shrink-0">
                    <i className={`${step.icon} text-orange-400 text-base`} />
                  </div>
                  <span className="text-zinc-600 text-xs font-black">0{i + 1}</span>
                </div>
                <h3 className="text-white text-sm font-bold mb-2">{step.title}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              Späť na hlavnú stránku
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 text-zinc-300 hover:text-white font-semibold px-7 py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-article-line" />
              Prečítajte si náš blog
            </Link>
          </div>
        </div>
      </main>

      {/* Footer strip */}
      <footer className="flex-shrink-0 border-t border-zinc-800/50 px-6 py-5">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-zinc-600 text-xs">
            © {new Date().getFullYear()} Boostio s.r.o. · hello@boostio.sk
          </p>
          <div className="flex items-center gap-5">
            <Link to="/ochrana-sukromia" className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors duration-200">
              Ochrana súkromia
            </Link>
            <Link to="/obchodne-podmienky" className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors duration-200">
              Obchodné podmienky
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
